import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Typography from 'LuxKit/Typography/Typography'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import LoyaltyLandingPageLargeTile from './LoyaltyLandingPageLargeTile'
import LoyaltyLandingPageSmallTile from './LoyaltyLandingPageSmallTile'
import LoyaltyLandingPageNextTierBenefitsContent from '../loyaltyUserAccountPage/LoyaltyLandingPageNextTierBenefitsContent'
import determineNextLoyaltyTier from 'loyalty/lib/determineNextLoyaltyTier'
import useLoyaltyLandingPageBenefitCopy from 'loyalty/hooks/useLoyaltyLandingPageBenefitCopy'
import { EmptyArray } from 'lib/array/arrayUtils'

const BenefitsSection = styled(Pane)`
  padding: ${rem(24)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(68)} 0;
  }
`

const SmallTilesSection = styled.div`
  display: grid;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

interface Props {
  onActivate?: () => void
}

function LoyaltyUserGoldBenefits(props: Props) {
  const { onActivate } = props
  const intl = useIntl()

  const isLoyaltyMember = useAppSelector(state => !!state.loyalty.account.data)

  const nextTier = useMemo(() => determineNextLoyaltyTier('gold'), [])

  const [loyaltyLandingPageBenefitTiles] = useLoyaltyLandingPageBenefitCopy()

  const largeTiles = useMemo(() => {
    if (!loyaltyLandingPageBenefitTiles) return EmptyArray
    return [
      loyaltyLandingPageBenefitTiles.airport_transfer,
      loyaltyLandingPageBenefitTiles.lounge_pass,
    ].filter(tile => tile?.enabled)
  }, [loyaltyLandingPageBenefitTiles])

  const smallTiles = useMemo(() => {
    if (!loyaltyLandingPageBenefitTiles) return EmptyArray
    return [
      loyaltyLandingPageBenefitTiles.phone_support,
      loyaltyLandingPageBenefitTiles.travelshoot_credit,
      loyaltyLandingPageBenefitTiles.tour_exp_credit,
    ].filter(tile => tile?.enabled)
  }, [loyaltyLandingPageBenefitTiles])

  return <BenefitsSection background="neutral-seven">
    <VerticalSpacer gap={48}>
      <VerticalSpacer gap={8}>
        <Heading variant="heading2" align="center">
          {intl.formatMessage({ id: 'VhsG5O8ktwKLAfMFYMDf', defaultMessage: 'Your Gold benefits, ready when you are', description: 'Heading for the gold benefits section on the loyalty landing page' })}
        </Heading>
        {!isLoyaltyMember && <BodyText variant="large" align="center">
          {intl.formatMessage({ id: 'qwB9bA4AUdrTwrd8ETbX', defaultMessage: 'Activate your membership toady to get instant access to these benefits', description: 'Body text for the gold benefits section on the loyalty landing page' })}
        </BodyText>}
      </VerticalSpacer>
      {loyaltyLandingPageBenefitTiles?.hotel_upgrade?.enabled && <LoyaltyLandingPageLargeTile
        tile={loyaltyLandingPageBenefitTiles.hotel_upgrade}
        onActivate={onActivate}
      />}
      {largeTiles.map((tile, index) => <LoyaltyLandingPageLargeTile
        reverse={index % 2 === 0}
        tile={tile}
        key={index}
      />)}
      <SmallTilesSection>
        {smallTiles.map(tile => <LoyaltyLandingPageSmallTile
          key={tile.image.id}
          tile={tile}
        />)}
      </SmallTilesSection>
      {nextTier && <LoyaltyLandingPageNextTierBenefitsContent nextTier={nextTier} />}
      {onActivate && <Typography align="center">
        <TextButton kind="primary" onClick={onActivate}>
          {intl.formatMessage({ id: 'p_vs4SgCX9_XSDAcil7r', defaultMessage: 'Activate your free membership', description: 'Button to activate free loyalty membership on the gold benefits section' })}
        </TextButton>
      </Typography>}
    </VerticalSpacer>
  </BenefitsSection>
}

export default LoyaltyUserGoldBenefits
